import React, { useState, useRef, useEffect, useMemo } from 'react';
import { calcCaja, promedioDiario, calcFlujo, fmtM, todayStr, COSTOS_FIJOS } from '../utils';
import { showToast } from '../components/UI';

const SYSTEM_PROMPT = `Sos el asistente de gestión de Farmacia Yacanto (Argentina). Registrás operaciones en el sistema con precisión y respondés en español, de forma corta y amigable. Sin markdown complejo, solo texto simple y emojis.

Cuando necesites registrar algo, poné el JSON al FINAL del mensaje en una línea separada así:
ACCION:{...}

ACCIONES DISPONIBLES:

1. VENTA:
ACCION:{"tipo":"venta","efectivo":200000,"transferencia":150000,"qr":0,"turno":"cierre_dia","obs":""}
Turnos: cierre_dia / cierre_manana / cierre_tarde / venta_externa / otro

2. GASTO VARIABLE:
ACCION:{"tipo":"gasto","monto":20000,"medio":"efectivo","categoria":"combustible","desc":"nafta"}
Categorias: combustible / local / marca / operativo / otro

3. COMPRA A CUENTA (sin pagar, con vencimiento):
ACCION:{"tipo":"compra_cuenta","proveedor":"COFARSUR","monto":500000,"fechaVenc":"2026-04-16","concepto":"medicamentos"}

4. COMPRA DE CONTADO (ya pagada):
ACCION:{"tipo":"compra_contado","proveedor":"COFARSUR","monto":50000,"medio":"efectivo","concepto":"medicamentos"}

5. PAGO DE COMPRA PENDIENTE:
ACCION:{"tipo":"pago_compra","proveedor":"COFARSUR","medio":"transferencia"}

6. PAGO DE COSTO FIJO:
ACCION:{"tipo":"pago_costo","nombre":"Alquiler","medio":"transferencia"}
Costos validos: DT Farmaceutica / Matricula Farmaceutica / Sistema GEMA / Alquiler / Luz / Internet / Monotributo / Amortizaciones / Impuesto municipal / Contadora

7. RETIRO DE SOCIO:
ACCION:{"tipo":"retiro_socio","socio":"Bauti","monto":100000,"medio":"efectivo"}
Socios validos: Bauti / Sol / Andy

REGLAS:
- "vendí 200000 efectivo y 150000 transferencia" → venta con esos montos
- "compré a COFARSUR 500000 vence 16/4" → compra_cuenta
- "pagué COFARSUR" → pago_compra
- "Bauti retiró 100000" → retiro_socio
- "gasté 20000 nafta transferencia" → gasto combustible
- Foto de hoja de ventas → sumar todos los montos y registrar como venta
- Si falta el medio de pago → preguntar efectivo o transferencia
- Si falta info esencial → preguntar antes de registrar
- No inventar datos`;


export default function ChatAsistente({ store, setItem, currentUser, compact }) {
  const { movimientos, compras, costosMes } = store;
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `¡Hola ${currentUser?.name || ''}! 👋 Soy tu asistente de farmacia. Podés decirme:\n\n• "Vendí $200 en efectivo y $150 en transferencia"\n• Mandar una foto de la hoja de ventas\n• "¿Cuánto puedo comprar esta semana?"\n• "Gasté $5000 en combustible"\n\n¿Qué necesitás?` }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [apiKey] = useState(() => localStorage.getItem('anthropic-key') || '');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const hoy = todayStr();
  const caja = useMemo(() => calcCaja(movimientos), [movimientos]);
  const prom20 = useMemo(() => promedioDiario(movimientos, 20), [movimientos]);
  const ajuste = parseFloat(localStorage.getItem('flujo-ajuste') || '0');
  const flujo = useMemo(() => calcFlujo(movimientos, compras, costosMes, ajuste), [movimientos, compras, costosMes, ajuste]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function getContextMessage() {
    return `Contexto actual de la farmacia (${hoy}):
- Caja efectivo: ${fmtM(caja.ef)}
- Caja cuentas: ${fmtM(caja.cuentas)}
- Caja total: ${fmtM(caja.total)}
- Promedio ventas diario (20d): ${fmtM(prom20)}
- Flujo proyectado al vencimiento: ${fmtM(flujo.solvencia)}
- Pagos pendientes en el período: ${fmtM(flujo.pagosEnPeriodo)}
- Máximo recomendado para comprar esta semana: ${fmtM(Math.max(0, flujo.solvencia))}`;
  }

  function handleImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImage(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text && !image) return;
    if (!apiKey) { showToast('Configurá la API key de Anthropic en Cuentas bancarias'); return; }

    const userContent = [];
    if (image) {
      userContent.push({ type: 'image', source: { type: 'base64', media_type: image.split(';')[0].split(':')[1], data: image.split(',')[1] } });
    }
    if (text) userContent.push({ type: 'text', text });
    else if (image) userContent.push({ type: 'text', text: 'Registrá las ventas que ves en esta imagen' });

    const newUserMsg = { role: 'user', content: text || '📷 Imagen enviada', image: image || null };
    setMessages(prev => [...prev, newUserMsg]);
    setInput('');
    setImage(null);
    setLoading(true);

    try {
      // Build messages for API (without the display-only fields)
      const apiMessages = [
        { role: 'user', content: [{ type: 'text', text: getContextMessage() }] },
        { role: 'assistant', content: 'Entendido, tengo el contexto de la farmacia.' },
        ...messages.slice(1).map(m => ({
          role: m.role,
          content: typeof m.content === 'string' ? m.content : m.content
        })),
        { role: 'user', content: userContent }
      ];

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1024,
          system: SYSTEM_PROMPT,
          messages: apiMessages
        })
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error.message);

      const fullText = data.content[0].text;

      // Parse and execute actions
      const actionLines = fullText.match(/^ACCION:\{.*\}$/gm) || [];
      const displayText = fullText.replace(/^ACCION:\{.*\}$/gm, '').trim();
      const actions = [];

      for (const line of actionLines) {
        try {
          const json = JSON.parse(line.replace('ACCION:', ''));
          actions.push(json);
          executeAction(json);
        } catch {}
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: displayText,
        actions
      }]);
    } catch(err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ Error: ' + err.message }]);
    }
    setLoading(false);
  }

  function executeAction(action) {
    const id = 'm' + Date.now() + Math.random().toString(36).slice(2,6);
    const hoyStr = todayStr();
    const hora = new Date().toTimeString().slice(0,5);

    if (action.tipo === 'venta') {
      const ef = parseFloat(action.efectivo) || 0;
      const tr = parseFloat(action.transferencia) || 0;
      const qr = parseFloat(action.qr) || 0;
      const total = ef + tr + qr;
      if (!total) return;
      const turnoLabels = { cierre_dia: 'Cierre del día', cierre_manana: 'Cierre mañana', cierre_tarde: 'Cierre tarde', venta_externa: 'Venta externa', otro: 'Otro' };
      setItem('movimientos', id, {
        id, tipo: 'venta', fecha: hoyStr, hora,
        monto: total, monto_efectivo: ef, monto_transferencia: tr, monto_qr: qr,
        medio: ef > 0 && tr === 0 && qr === 0 ? 'efectivo' : 'mixto',
        signo: '+', turno: action.turno || 'cierre_dia',
        desc: turnoLabels[action.turno] || 'Chat' + (action.obs ? ' — ' + action.obs : ''),
        autor: currentUser?.name || 'chat'
      });
      showToast('✓ Venta registrada');

    } else if (action.tipo === 'gasto') {
      const monto = parseFloat(action.monto) || 0;
      if (!monto) return;
      const catLabels = { combustible: 'Combustible', local: 'Inversiones en local', marca: 'Inversión en marca', operativo: 'Gastos operativos', otro: 'Otro' };
      setItem('movimientos', id, {
        id, tipo: 'gasto', fecha: hoyStr, hora,
        monto, medio: action.medio || 'efectivo', signo: '-',
        categoria: action.categoria || 'otro',
        categoriaLabel: catLabels[action.categoria] || 'Otro',
        desc: action.desc || action.categoria || 'Gasto',
        autor: currentUser?.name || 'chat'
      });
      showToast('✓ Gasto registrado');

    } else if (action.tipo === 'compra_cuenta') {
      const monto = parseFloat(action.monto) || 0;
      if (!monto || !action.proveedor) return;
      const cid = 'c' + Date.now();
      setItem('compras', cid, {
        id: cid, proveedor: action.proveedor.toUpperCase(),
        monto, fecha: hoyStr, fechaVenc: action.fechaVenc || '',
        concepto: action.concepto || '', pagado: false, fechaPago: ''
      });
      showToast('✓ Compra a cuenta registrada');

    } else if (action.tipo === 'compra_contado') {
      const monto = parseFloat(action.monto) || 0;
      if (!monto || !action.proveedor) return;
      setItem('movimientos', id, {
        id, tipo: 'compra', fecha: hoyStr, hora,
        monto, medio: action.medio || 'transferencia', signo: '-',
        desc: (action.proveedor || '') + (action.concepto ? ' — ' + action.concepto : ''),
        proveedor: action.proveedor.toUpperCase(), autor: currentUser?.name || 'chat'
      });
      showToast('✓ Compra de contado registrada');

    } else if (action.tipo === 'pago_compra') {
      // Buscar la compra pendiente del proveedor
      const comprasPendientes = Object.entries(store.compras || {})
        .filter(([, c]) => !c.pagado && c.proveedor?.toUpperCase() === action.proveedor?.toUpperCase());
      if (!comprasPendientes.length) { showToast('No se encontró compra pendiente de ' + action.proveedor); return; }
      const [cid, compra] = comprasPendientes[0];
      setItem('movimientos', id, {
        id, tipo: 'pago_proveedor', fecha: hoyStr, hora,
        monto: compra.monto, medio: action.medio || 'transferencia', signo: '-',
        desc: `Pago ${compra.proveedor}${compra.concepto ? ' — ' + compra.concepto : ''}`,
        proveedor: compra.proveedor, autor: currentUser?.name || 'chat'
      });
      setItem('compras', cid, { ...compra, pagado: true, fechaPago: hoyStr, medioPago: action.medio || 'transferencia' });
      showToast('✓ Pago registrado — ' + compra.proveedor);

    } else if (action.tipo === 'pago_costo') {
      const costo = store.costosMes?.[action.nombre];
      if (!costo) { showToast('No se encontró el costo: ' + action.nombre); return; }
      setItem('movimientos', id, {
        id, tipo: 'pago_proveedor', fecha: hoyStr, hora,
        monto: parseFloat(costo.monto) || 0, medio: action.medio || 'transferencia', signo: '-',
        desc: `Pago ${action.nombre}`, concepto: action.nombre, autor: currentUser?.name || 'chat'
      });
      setItem('costosMes', action.nombre, { ...costo, pagado: true, fechaPago: hoyStr, medioPago: action.medio || 'transferencia' });
      showToast('✓ Costo pagado — ' + action.nombre);

    } else if (action.tipo === 'retiro_socio') {
      const monto = parseFloat(action.monto) || 0;
      if (!monto || !action.socio) return;
      setItem('movimientos', id, {
        id, tipo: 'retiro', fecha: hoyStr, hora,
        monto, medio: action.medio || 'efectivo', signo: '-',
        desc: `Retiro ${action.socio}`, socio: action.socio,
        autor: currentUser?.name || 'chat'
      });
      // Actualizar saldo del socio
      const socioEntry = Object.entries(store.socios || {}).find(([, s]) => s.nombre === action.socio);
      if (socioEntry) {
        const [sid, socioData] = socioEntry;
        const mid = 'sr' + Date.now();
        setItem('socios', sid, {
          ...socioData,
          saldo: (parseFloat(socioData.saldo) || 0) - monto,
          movimientos: { ...socioData.movimientos, [mid]: { tipo: 'retiro', monto, fecha: hoyStr } }
        });
      }
      showToast('✓ Retiro registrado — ' + action.socio);
    }
  }


  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: compact ? '100%' : 600, margin: compact ? 0 : '0 auto' }}>
      {/* Header */}
      {!compact && <div style={{ padding: '12px 16px', background: 'var(--grn)', borderRadius: 'var(--rl)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 24 }}>💊</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Asistente Farmacia</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.7)' }}>Cargá ventas y consultá datos</div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,.8)', textAlign: 'right' }}>
          <div>Caja: {fmtM(caja.total)}</div>
          <div>Prom: {fmtM(prom20)}/día</div>
        </div>
      </div>}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 10 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '82%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.role === 'user' ? 'var(--grn)' : 'var(--surf)',
              color: msg.role === 'user' ? '#fff' : 'var(--tx)',
              border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
              fontSize: 13, lineHeight: 1.5,
            }}>
              {msg.image && <img src={msg.image} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 6, maxHeight: 120, objectFit: 'cover' }} />}
              <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              {msg.actions?.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.2)' }}>
                  {msg.actions.map((a, j) => (
                    <div key={j} style={{ fontSize: 11, background: 'rgba(255,255,255,.15)', borderRadius: 6, padding: '3px 8px', display: 'inline-block', marginRight: 4 }}>
                      ✓ {a.tipo === 'venta' ? `Venta ${fmtM((parseFloat(a.efectivo)||0)+(parseFloat(a.transferencia)||0)+(parseFloat(a.qr)||0))}`
                        : a.tipo === 'gasto' ? `Gasto ${fmtM(a.monto)}`
                        : a.tipo === 'compra_cuenta' ? `Compra ${a.proveedor} ${fmtM(a.monto)}`
                        : a.tipo === 'compra_contado' ? `Compra contado ${a.proveedor} ${fmtM(a.monto)}`
                        : a.tipo === 'pago_compra' ? `Pago ${a.proveedor}`
                        : a.tipo === 'pago_costo' ? `Pago ${a.nombre}`
                        : a.tipo === 'retiro_socio' ? `Retiro ${a.socio} ${fmtM(a.monto)}`
                        : a.tipo}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '10px 16px', background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: '16px 16px 16px 4px', fontSize: 20 }}>
              ⏳
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Image preview */}
      {image && (
        <div style={{ padding: '8px 12px', background: 'var(--grn-l)', borderRadius: 8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={image} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
          <div style={{ flex: 1, fontSize: 12, color: 'var(--grn)', fontWeight: 600 }}>Imagen lista para enviar</div>
          <button onClick={() => setImage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 18 }}>×</button>
        </div>
      )}

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <label style={{ cursor: 'pointer', padding: '10px', borderRadius: 10, background: 'var(--surf2)', border: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <input type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
          <span style={{ fontSize: 20 }}>📷</span>
        </label>
        <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder="Escribí una venta, gasto o consulta... (Enter para enviar)"
          rows={1}
          style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border2)', fontSize: 14, fontFamily: 'Arial, sans-serif', background: 'var(--surf)', color: 'var(--tx)', outline: 'none', resize: 'none', lineHeight: 1.4, maxHeight: 100, overflowY: 'auto' }} />
        <button onClick={sendMessage} disabled={loading || (!input.trim() && !image)}
          style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: loading || (!input.trim() && !image) ? 'var(--tx3)' : 'var(--grn)', color: '#fff', cursor: loading ? 'default' : 'pointer', fontSize: 20, flexShrink: 0 }}>
          ➤
        </button>
      </div>
    </div>
  );
}
